/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  CheckboxOnChangeData,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  InputOnChangeData,
  Label,
  makeStyles,
  mergeClasses,
  Option,
  Switch,
  SwitchOnChangeData,
  useId,
} from "@fluentui/react-components";
import { ChevronDownFilled, ChevronRightFilled } from "@fluentui/react-icons";
import { ActivityContent } from "@skanska/vsa-activity-content";
import { useMsal } from "@skanska/vsa-client-auth";
import {
  ChangeEvent,
  Fragment,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  KmaChapterFieldUniqueName,
  ProcessFieldUniqueName,
} from "../../constants/Fields";
import {
  useCancelActivityDraft,
  useGetUsedPaths,
} from "../../hooks/useActivities";
import { useActivityImageData } from "../../hooks/useActivityImageData";
import { useApplyTableWidths } from "../../hooks/useApplyTableWidths";
import { useEditorImageResolver } from "../../hooks/useEditorImageResolver";
import { useOptionalTagsGet } from "../../hooks/useTags";
import { useGetAllCurrentPublishedTools } from "../../hooks/useTools";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import {
  Activity,
  ActivityFormData,
  ProcessFlowHeadSettings,
  ProcessFlowSettings,
} from "../../models/Activity";
import { Chapter } from "../../models/Chapter";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Field as FieldModel } from "../../models/Field";
import { FieldTypes } from "../../models/FieldTypes";
import { Tool } from "../../models/Tool";
import { FlatVsaaTag, VsaaTag } from "../../models/VsaaTag";
import { VsaaColorTokens } from "../../style/VsaaColorTokens";
import {
  canConfigureProcessFlowForActivity,
  getProcessFlowSettingsForRequest,
  getProcessFlowHeadSettings,
  getProcessFlowHeadSettingsForRequest,
  getProcessIdsWithSubactivities,
  getPublishedToolFiltersForActivity,
  getProcessFlowSettings,
  isActivityFormValid,
  parseImageDataFromHtml,
  parseToolLinkIdsFromHtml,
  shouldInheritProcessFlowSettingsForRequest,
} from "../../services/ActivityService";
import { validateFormFieldsForRequest } from "../../services/FieldService";
import { replaceImageSrcWithImageIds } from "../../services/ImageService";
import { CheckoutMessageBar } from "../CheckoutMessageBar";
import { DisplayField } from "../DisplayField";
import { SaveSplitButton } from "../SaveSplitButton";
import MUIEditor from "../editor/MUIEditor";
import { StickyTopBannerStack } from "../StickyTopBannerStack";
import { VsaaTagPicker } from "../tagpicker/VsaaTagPicker";
import GroupedToolList from "../tool/GroupedToolList";
import ToolPickerDialog from "../tool/ToolPickerDialog";
import { ActivityDeactivationWarningDialog } from "./ActivityDeactivationWarningDialog";
import { ActivityPublishDialog } from "./ActivityPublishDialog";
import { ActivityWithMirroredChildrenMessageBar } from "./ActivityWithMirroredChildrenMessageBar";
import { CancelDraftDialog } from "./CancelDraftDialog";
import { MirroredActivityMessageBar } from "./MirroredActivityMessageBar";
import { useActivtyContentClasses } from "./useActivtyContentClasses";
import { useGetUserAccessLevel } from "../../hooks/useGetUserAccessLevel";
import { useChaptersGetAll } from "../../hooks/useChapter";

interface ActivityFormProps {
  existingActivity?: Activity | undefined;
  activityFields?: FieldModel[];
  initialOpenAccordionSections?: ActivityFormOpenAccordionSections;
  onOpenAccordionSectionsChange?: (
    openAccordionSections: ActivityFormOpenAccordionSections
  ) => void;
  onSubmit: (
    data: ActivityFormData,
    options?: { publishAfterSave?: boolean }
  ) => void | Promise<void>;
  isLoading: boolean;
  isRefetching: boolean;
  /** When true, automatically opens the publish dialog once the activity has loaded (used after a combined save-and-publish create). */
  autoOpenPublishDialog?: boolean;
}

export interface ActivityFormOpenAccordionSections {
  processFlow: boolean;
  tags: boolean;
  tools: boolean;
}

interface SelectedToolsOverride {
  activityVersionId?: string;
  tools: Tool[];
}

const processActivityShapeOptions: {
  value: ProcessFlowSettings["visibility"];
  labelKey: string;
}[] = [
  {
    value: "currentAndChild",
    labelKey: "processFlow.activityShape.rectangle",
  },
  { value: "hidden", labelKey: "processFlow.activityShape.hidden" },
];

const processConnectionOptions: {
  value: ProcessFlowSettings["showArrowToNextStep"];
  labelKey: string;
}[] = [
  { value: false, labelKey: "processFlow.connection.none" },
  { value: true, labelKey: "processFlow.connection.arrow" },
];

const processFlowHeadShapeOptions: {
  value: ProcessFlowSettings["activityShape"];
  labelKey: string;
}[] = [{ value: "rectangle", labelKey: "processFlow.activityShape.rectangle" }];

const getSelectedTools = (
  selectedToolsOverride: SelectedToolsOverride | undefined,
  activityVersionId: string | undefined,
  fetchedTools: Tool[] | undefined
): Tool[] => {
  if (
    selectedToolsOverride !== undefined &&
    selectedToolsOverride.activityVersionId === activityVersionId
  ) {
    return selectedToolsOverride.tools;
  }

  return fetchedTools ?? [];
};

const getActivityFormData = (
  existingActivity: Activity | undefined
): ActivityFormData => ({
  title: existingActivity ? existingActivity.title : "",
  richText: existingActivity
    ? (existingActivity.description?.serializedHtml ?? "")
    : "",
  fieldsWithValues: {
    ...existingActivity?.fieldValues?.booleans,
    ...existingActivity?.fieldValues?.tags,
    ...existingActivity?.fieldValues?.users,
    ...existingActivity?.fieldValues?.stringArrays,
  },
  toolIds: existingActivity?.toolIds,
  mirroredActivityId: existingActivity?.mirroredActivityId,
  processesIdsWithSubactivities:
    existingActivity?.processesIdsWithSubactivities || [],
  imageIds: existingActivity?.description?.imageIds || [],
  imageVersionIds: existingActivity?.description?.imageVersionIds || [],
  processFlowSettings: existingActivity?.processFlowSettings,
  processFlowHeadSettings: existingActivity?.processFlowHeadSettings,
});

const hasKmaVariantTag = (chapter: Chapter): boolean => {
  return Boolean(chapter.KMACollaborationVariant?.id);
};

const useStyles = makeStyles({
  root: {
    gap: "10px",
    display: "flex",
    flexDirection: "column",
    marginBottom: "20px",
  },
  formContainer: {
    display: "grid",
    columnGap: "42px",
    gridTemplateColumns: "2fr 3fr",
  },
  formSpacingContainer: {
    margin: "20px 0",
    display: "flex",
    flexDirection: "column",
  },
  toggleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "12px",
  },
  checkboxContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
  },
  processFlowGrid: {
    display: "grid",
    gap: "16px",
    gridTemplateColumns: "1fr 1fr",
  },
  processFlowSectionDivider: {
    borderTop: `1px solid ${VsaaColorTokens.color_process_flow_section_divider}`,
    gridColumn: "1 / -1",
    margin: "20px 12px 12px",
  },
  processFlowFullWidth: {
    gridColumn: "1 / -1",
  },
  processFlowOptions: {
    display: "grid",
    gap: "16px",
    gridTemplateColumns: "1fr 1fr",
    marginTop: "16px",
  },
  preview: {
    padding: "16px",
    paddingLeft: "40px",
    height: "100%",
    borderLeft: "3px solid #000",
  },
  actionButtons: {
    marginTop: "20px",
    justifyContent: "center",
    display: "flex",
    gap: "10px",
  },
  editArea: {
    minWidth: "500px",
  },
  accordionHeader: {
    borderBottom: "1px solid #DDD",
  },
  previewPlaceholderText: {
    color: "#DDD",
    "& p.preview-placeholder": {
      fontSize: "32px !important",
    },
  },
  activityStatusContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const ActivityForm = (props: ActivityFormProps) => {
  const {
    existingActivity,
    onSubmit,
    isLoading: activityLoading,
    activityFields,
    isRefetching: activityRefetching,
    autoOpenPublishDialog,
    initialOpenAccordionSections,
    onOpenAccordionSectionsChange,
  } = props;

  const contentRef = useRef<HTMLDivElement>(null);

  // Apply table widths from data-table-width attributes
  useApplyTableWidths(contentRef, existingActivity);

  const { data: toolsResult, error } = useGetAllCurrentPublishedTools(
    "title",
    true,
    getPublishedToolFiltersForActivity(existingActivity),
    150
  );

  if (error) {
    console.error("Error fetching tools:", error);
  }

  const { data: usedPaths } = useGetUsedPaths(existingActivity?.activityId);
  const existingActivityTags = existingActivity?.fieldValues?.tags;

  const filteredUsedPaths = useMemo(() => {
    if (!usedPaths || !existingActivityTags) {
      return usedPaths;
    }

    const currentActivityTagIds: string[] = [];
    Object.values(existingActivityTags).forEach((fieldValue) => {
      if (Array.isArray(fieldValue)) {
        fieldValue.forEach((tag) => {
          if (tag?.id) {
            currentActivityTagIds.push(tag.id);
          }
        });
      }
    });

    // Filter current activity
    return usedPaths.filter((tagId) => !currentActivityTagIds.includes(tagId));
  }, [usedPaths, existingActivityTags]);

  const [formData, setFormData] = useState<ActivityFormData>(() =>
    getActivityFormData(existingActivity)
  );
  const [hasFormAction, setHasFormAction] = useState(false);
  const [inheritProcessFlowSettings, setInheritProcessFlowSettings] = useState(
    existingActivity?.processFlowSettings == null
  );
  const processFlowSettingsBeforeInheritanceChange = useRef(
    existingActivity?.processFlowSettings ?? undefined
  );

  const [selectedToolsOverride, setSelectedToolsOverride] =
    useState<SelectedToolsOverride>();

  const selectedTools = getSelectedTools(
    selectedToolsOverride,
    existingActivity?.versionId,
    toolsResult?.items
  );
  const resolvedImages = useEditorImageResolver(formData.imageIds ?? []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishOpenSignal, setPublishOpenSignal] = useState(0);
  const hasAutoOpenedPublishDialog = useRef(false);
  const [tagSectionOpen, setTagSectionOpen] = useState<boolean>(
    initialOpenAccordionSections?.tags ?? false
  );
  const [toolsSectionOpen, setToolsSectionOpen] = useState<boolean>(
    initialOpenAccordionSections?.tools ?? false
  );
  const [processFlowSectionOpen, setProcessFlowSectionOpen] = useState<boolean>(
    initialOpenAccordionSections?.processFlow ?? false
  );
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [cancelCheckedOutDialogOpen, setCancelCheckedOutDialogOpen] =
    useState<boolean>(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState<boolean>(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<
    "subActivities" | "mirroredChildren" | "mirroredChildrenProcess"
  >("subActivities");
  const [warningType] = useState<"subActivities" | "mirroredChildren">(
    "subActivities"
  );

  const memoizedPreviewContent = useMemo(() => {
    return (
      <ActivityContent
        htmlContent={formData.richText}
        useImageData={useActivityImageData}
      ></ActivityContent>
    );
  }, [formData.richText]);

  const showError = (
    type: "subActivities" | "mirroredChildren" | "mirroredChildrenProcess"
  ) => {
    setErrorType(type);
    setErrorDialogOpen(true);
  };

  const canModifyActiveField = (): {
    canModify: boolean;
    errorType?: "subActivities" | "mirroredChildren";
  } => {
    if (
      existingActivity?.processesIdsWithSubactivities &&
      existingActivity?.processesIdsWithSubactivities.length > 0
    ) {
      return { canModify: false, errorType: "subActivities" };
    }

    if (
      existingActivity?.mirroredChildren &&
      existingActivity?.mirroredChildren?.length > 0
    ) {
      return { canModify: false, errorType: "mirroredChildren" };
    }

    return { canModify: true };
  };

  const removesLastPositionWithMirroredChildren = (
    removedProcessIds: string[],
    currentProcessIds: string[]
  ): boolean => {
    return (
      currentProcessIds.length > 0 &&
      removedProcessIds.length - currentProcessIds.length === 0 &&
      (existingActivity?.mirroredChildren?.length ?? 0) > 0
    );
  };

  const classes = useStyles();
  const contentClasses = useActivtyContentClasses();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const currentUser = instance.getActiveAccount();

  const accessLevel = useGetUserAccessLevel();

  const { data: chapters } = useChaptersGetAll();

  const kmaVariantChapters = useMemo(() => {
    return chapters?.filter(hasKmaVariantTag) ?? [];
  }, [chapters]);

  const { mutateAsync: cancelDraft, isPending: cancelDraftPending } =
    useCancelActivityDraft(existingActivity?.versionId || "");

  const isCurrentUsersDraft = useMemo<boolean>(() => {
    if (!existingActivity) {
      return true;
    }

    const userEntraId = currentUser?.idTokenClaims?.oid;

    if (!userEntraId) {
      return false;
    }

    if (userEntraId === existingActivity.createdBy?.id) {
      return true;
    }

    return false;
  }, [currentUser, existingActivity]);

  const activeField = useMemo<FieldModel | undefined>(() => {
    if (!activityFields || activityFields.length === 0) {
      return;
    }

    const activeField = activityFields.filter((f) => f.uniqueName === "active");

    return activeField[0];
  }, [activityFields]);

  const archivedField = useMemo<FieldModel | undefined>(() => {
    if (!activityFields || activityFields.length === 0) {
      return;
    }

    const archivedField = activityFields.filter(
      (f) => f.uniqueName === "archived"
    );

    return archivedField[0];
  }, [activityFields]);

  const processField = useMemo<FieldModel | undefined>(() => {
    return activityFields?.find(
      (field) => field.uniqueName === ProcessFieldUniqueName
    );
  }, [activityFields]);

  const { data: allProcessTags } = useOptionalTagsGet(
    processField?.tagCollectionId
  );

  const processFlowFormData = useMemo<ActivityFormData>(() => {
    const processTags = (formData.fieldsWithValues?.[ProcessFieldUniqueName] ??
      []) as FlatVsaaTag[];
    const activityProcessIds = filteredUsedPaths ?? [];
    const processesIdsWithSubactivities =
      allProcessTags && allProcessTags.length > 0
        ? getProcessIdsWithSubactivities(
            processTags,
            allProcessTags,
            activityProcessIds
          )
        : (formData.processesIdsWithSubactivities ?? []);

    return {
      ...formData,
      processesIdsWithSubactivities,
    };
  }, [allProcessTags, filteredUsedPaths, formData]);

  // Check if the content has any value. If the content field is cleared it leaves a empty p tag.
  const contentIsEmpty =
    !formData.richText ||
    formData.richText === '<p style="text-align: paragraph"></p>';

  const saveEnabled =
    hasFormAction &&
    isActivityFormValid({
      formData: processFlowFormData,
      activityFields,
    });

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    newValue: InputOnChangeData
  ): void => {
    const name = event.target.name;

    if (name === "active") {
      const { canModify, errorType } = canModifyActiveField();
      if (!canModify && errorType) {
        showError(errorType);
        return;
      }
    }

    setHasFormAction(true);
    setFormData((formData: any) => ({
      ...formData,
      [name]:
        newValue.value !== undefined ? newValue.value : event.target.value,
    }));
  };

  const handleSubmit = async (event: any): Promise<void> => {
    event.preventDefault();

    if (!saveEnabled) {
      return;
    }
    setIsSubmitting(true);

    try {
      await onSubmit(processFlowFormData);
      setHasFormAction(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndPublish = async (): Promise<void> => {
    if (!existingActivity) {
      if (!saveEnabled) {
        return;
      }

      setIsSubmitting(true);

      try {
        await onSubmit(processFlowFormData, { publishAfterSave: true });
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    setPublishOpenSignal((signal) => signal + 1);
  };

  const pendingActivityUpdate = useMemo(() => {
    const requestFields = validateFormFieldsForRequest(
      processFlowFormData.fieldsWithValues,
      activityFields ?? []
    );

    const serializedHtml = processFlowFormData.richText
      ? replaceImageSrcWithImageIds(processFlowFormData.richText)
      : processFlowFormData.richText;

    return {
      title: processFlowFormData.title,
      serializedHtml,
      fieldsWithValues: requestFields,
      toolIds: processFlowFormData.toolIds,
      mirroredActivityId: processFlowFormData.mirroredActivityId,
      imageIds: processFlowFormData.imageIds,
      imageVersionIds: processFlowFormData.imageVersionIds,
      toolLinkIds: processFlowFormData.toolLinkIds,
      processFlowSettings:
        getProcessFlowSettingsForRequest(processFlowFormData),
      processFlowHeadSettings:
        getProcessFlowHeadSettingsForRequest(processFlowFormData),
      inheritProcessFlowSettings:
        shouldInheritProcessFlowSettingsForRequest(processFlowFormData),
    };
  }, [activityFields, processFlowFormData]);

  useEffect(() => {
    if (
      autoOpenPublishDialog &&
      existingActivity &&
      !hasAutoOpenedPublishDialog.current
    ) {
      hasAutoOpenedPublishDialog.current = true;
      setPublishOpenSignal((signal) => signal + 1);
    }
  }, [autoOpenPublishDialog, existingActivity]);

  const handleRichTextChange = (value: string): void => {
    setHasFormAction(true);
    setFormData((formData) => {
      const { imageIds, imageVersionIds } = parseImageDataFromHtml(value);
      const toolLinkIds = parseToolLinkIdsFromHtml(value);

      return {
        ...formData,
        richText: value,
        imageIds: imageIds,
        imageVersionIds: imageVersionIds,
        toolLinkIds: toolLinkIds,
      };
    });
  };

  const handleProcessFlowSettingChange = <
    TKey extends keyof ProcessFlowSettings,
  >(
    key: TKey,
    value: ProcessFlowSettings[TKey]
  ): void => {
    setHasFormAction(true);
    setInheritProcessFlowSettings(false);
    setFormData((previousFormData) => {
      const processFlowSettings = {
        ...getProcessFlowSettings(previousFormData.processFlowSettings),
        [key]: value,
      };
      processFlowSettingsBeforeInheritanceChange.current = processFlowSettings;

      return {
        ...previousFormData,
        processFlowSettings,
      };
    });
  };

  const handleProcessFlowInheritanceChange = (inherit: boolean): void => {
    setHasFormAction(true);
    setInheritProcessFlowSettings(inherit);
    setFormData((previousFormData) => {
      if (!inherit) {
        return {
          ...previousFormData,
          processFlowSettings: getProcessFlowSettings(
            previousFormData.processFlowSettings ??
              processFlowSettingsBeforeInheritanceChange.current
          ),
        };
      }

      if (previousFormData.processFlowSettings != null) {
        processFlowSettingsBeforeInheritanceChange.current =
          previousFormData.processFlowSettings;
      }

      return {
        ...previousFormData,
        processFlowSettings: undefined,
      };
    });
  };

  const handleAccordionSectionChange = (
    section: keyof ActivityFormOpenAccordionSections,
    isOpen: boolean
  ): void => {
    if (section === "processFlow") {
      setProcessFlowSectionOpen(isOpen);
    } else if (section === "tags") {
      setTagSectionOpen(isOpen);
    } else {
      setToolsSectionOpen(isOpen);
    }

    onOpenAccordionSectionsChange?.({
      processFlow: section === "processFlow" ? isOpen : processFlowSectionOpen,
      tags: section === "tags" ? isOpen : tagSectionOpen,
      tools: section === "tools" ? isOpen : toolsSectionOpen,
    });
  };

  const handleProcessFlowHeadChange = (isProcessFlowHead: boolean): void => {
    setHasFormAction(true);
    setFormData((previousFormData) => ({
      ...previousFormData,
      processFlowHeadSettings: isProcessFlowHead
        ? getProcessFlowHeadSettings(previousFormData.processFlowHeadSettings)
        : undefined,
    }));
  };

  const handleProcessFlowHeadSettingChange = <
    TKey extends keyof ProcessFlowHeadSettings,
  >(
    key: TKey,
    value: ProcessFlowHeadSettings[TKey]
  ): void => {
    setHasFormAction(true);
    setFormData((previousFormData) => ({
      ...previousFormData,
      processFlowHeadSettings: {
        ...getProcessFlowHeadSettings(previousFormData.processFlowHeadSettings),
        [key]: value,
      },
    }));
  };

  const handleFieldChange = (
    fieldName: string,
    value: FlatVsaaTag[] | boolean | string[]
  ): void => {
    if (fieldName === "active") {
      const { canModify, errorType } = canModifyActiveField();
      if (!canModify && errorType) {
        showError(errorType);
        return;
      }
    }

    if (fieldName === "archived") {
      const { canModify, errorType } = canModifyActiveField();
      if (!canModify && errorType) {
        showError(errorType);
        return;
      }
    }

    // For process field, only block removal of existing placements
    if (fieldName === "process" && Array.isArray(value)) {
      const newProcesses = value as FlatVsaaTag[];
      const currentProcesses =
        (formData.fieldsWithValues?.["process"] as FlatVsaaTag[]) || [];
      const currentProcessIds = currentProcesses.map((p) => p.id);
      const newProcessIds = newProcesses.map((p) => p.id);

      const removedProcessIds = currentProcessIds.filter(
        (id) => !newProcessIds.includes(id)
      );

      if (
        removesLastPositionWithMirroredChildren(
          removedProcessIds,
          currentProcessIds
        )
      ) {
        showError("mirroredChildrenProcess");
        return;
      }

      if (
        removedProcessIds.length > 0 &&
        existingActivity?.processesIdsWithSubactivities &&
        removedProcessIds.some((id) =>
          existingActivity.processesIdsWithSubactivities?.includes(id)
        )
      ) {
        showError("subActivities");
        return;
      }
    }

    setHasFormAction(true);
    const newFieldsWithValues = formData.fieldsWithValues
      ? JSON.parse(JSON.stringify(formData.fieldsWithValues))
      : {};

    newFieldsWithValues[fieldName] = value;

    // When archived is set to true, automatically deactivate
    if (fieldName === "archived" && value === true) {
      newFieldsWithValues["active"] = false;
    }

    setFormData((formData) => ({
      ...formData,
      fieldsWithValues: newFieldsWithValues,
    }));
  };
  useUnsavedChanges(
    saveEnabled && !isSubmitting && !isPublishing,
    t("unsavedChangesMessage")
  );

  // Stops Enter action from clearing other fields.
  const preventEnter = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleFileSelect = (tool?: Tool) => {
    if (!tool) {
      return;
    }

    setHasFormAction(true);

    const alreadySelected = selectedTools.filter(
      (t) => t.toolId === tool.toolId
    );

    if (alreadySelected.length > 0) {
      return;
    }

    const updatedTools = [...selectedTools, tool];

    setSelectedToolsOverride({
      activityVersionId: existingActivity?.versionId,
      tools: updatedTools,
    });

    setFormData((prevFormData) => ({
      ...prevFormData,
      toolIds: updatedTools
        .map((tool) => tool.toolId)
        .filter((id): id is string => id !== undefined),
    }));
  };

  const handleLinkToolInsert = (tool: Tool) => {
    setHasFormAction(true);
    const alreadySelected = selectedTools.some((t) => t.toolId === tool.toolId);

    if (alreadySelected) {
      return;
    }

    const updatedTools = [...selectedTools, tool];

    setSelectedToolsOverride({
      activityVersionId: existingActivity?.versionId,
      tools: updatedTools,
    });

    setFormData((prevFormData) => ({
      ...prevFormData,
      toolIds: updatedTools
        .map((tool) => tool.toolId)
        .filter((id): id is string => id !== undefined),
    }));
  };

  const handleLinkToolRemove = (toolId: string) => {
    setHasFormAction(true);
    // Remove the tool from selectedTools when link is removed from editor
    // Use functional setState to handle multiple simultaneous deletions correctly
    setSelectedToolsOverride((previousOverride) => {
      const prevTools =
        previousOverride !== undefined &&
        previousOverride.activityVersionId === existingActivity?.versionId
          ? previousOverride.tools
          : selectedTools;
      const updatedTools = prevTools.filter((tool) => tool.toolId !== toolId);

      // Only update if something changed
      if (updatedTools.length !== prevTools.length) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          toolIds: updatedTools
            .map((tool) => tool.toolId)
            .filter((id): id is string => id !== undefined),
        }));
      }

      return {
        activityVersionId: existingActivity?.versionId,
        tools: updatedTools,
      };
    });
  };

  const handleToolRemove = (toolId: string) => {
    setHasFormAction(true);
    const updatedTools = selectedTools.filter((tool) => tool.toolId !== toolId);

    setSelectedToolsOverride({
      activityVersionId: existingActivity?.versionId,
      tools: updatedTools,
    });

    setFormData((prevFormData) => {
      // Update the HTML to make the link inactive for this tool
      const parser = new DOMParser();
      const doc = parser.parseFromString(
        prevFormData.richText || "",
        "text/html"
      );
      const links = doc.querySelectorAll(`a[data-tool-link-id="${toolId}"]`);

      links.forEach((link) => {
        // Replace the link gray span
        const span = doc.createElement("span");
        span.textContent = link.textContent;
        span.setAttribute("style", "color: #666;");
        link.parentNode?.replaceChild(span, link);
      });

      const updatedHtml = doc.body.innerHTML;

      // Re-parse toolLinkIds from the updated HTML
      const updatedToolLinkIds = parseToolLinkIdsFromHtml(updatedHtml);

      return {
        ...prevFormData,
        richText: updatedHtml,
        toolLinkIds: updatedToolLinkIds,
        toolIds: updatedTools
          .map((tool) => tool.toolId)
          .filter((id): id is string => id !== undefined),
      };
    });
  };

  const handleCancelDraftClick = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelDraftConfirm = async () => {
    if (!isCurrentUsersDraft) {
      setCancelDialogOpen(false);
      setCancelCheckedOutDialogOpen(true);
      return;
    }

    if (existingActivity) {
      try {
        await cancelDraft();
        setCancelDialogOpen(false);
        navigate(ClientRoutes.ActivitiesDrafts);
      } catch (error) {
        console.error("Error canceling draft:", error);
      }
    }
  };

  const handleCancelDraftCancel = () => {
    setCancelDialogOpen(false);
  };

  const handleCancelCheckedOutDraftConfirm = async () => {
    if (existingActivity) {
      try {
        await cancelDraft();
        setCancelCheckedOutDialogOpen(false);
        navigate(ClientRoutes.ActivitiesDrafts);
      } catch (error) {
        console.error("Error canceling draft:", error);
      }
    }
  };

  const handleCancelCheckedOutDraftCancel = () => {
    setCancelCheckedOutDialogOpen(false);
  };

  const handleWarningDialogClose = () => {
    setWarningDialogOpen(false);
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
  };

  const activityTitleId = useId("activityTitle");
  const hasMirroredChildren =
    (existingActivity?.mirroredChildren?.length ?? 0) > 0;
  const hasStickyTopBanners =
    (!isCurrentUsersDraft && existingActivity && existingActivity.createdBy) ||
    Boolean(existingActivity?.mirroredActivityId) ||
    hasMirroredChildren;

  const getTitleForPreview = (): string => {
    if (!formData?.fieldsWithValues || !formData.fieldsWithValues["process"]) {
      return "";
    }

    return (formData.fieldsWithValues!["process"] as VsaaTag[])[0]?.title;
  };

  const processFlowSettings = getProcessFlowSettings(
    formData.processFlowSettings
  );
  const canConfigureProcessFlow =
    canConfigureProcessFlowForActivity(processFlowFormData);
  const hasSelectedProcess = Boolean(
    (
      processFlowFormData.fieldsWithValues?.[ProcessFieldUniqueName] as
        FlatVsaaTag[] | undefined
    )?.length
  );
  const shouldShowProcessFlowSection =
    accessLevel >= 3 && (canConfigureProcessFlow || !hasSelectedProcess);
  const isProcessFlowHead = formData.processFlowHeadSettings != null;
  const processFlowHeadSettings = getProcessFlowHeadSettings(
    formData.processFlowHeadSettings
  );

  const getProcessFlowOptionLabel = <
    TOption extends { value: string | boolean; labelKey: string },
  >(
    options: TOption[],
    selectedValue: string | boolean
  ): string => {
    const option = options.find((item) => item.value === selectedValue);
    return option ? t(option.labelKey) : "";
  };

  return (
    <div className={classes.root}>
      {hasStickyTopBanners && (
        <StickyTopBannerStack>
          {!isCurrentUsersDraft &&
            existingActivity &&
            existingActivity.createdBy && (
              <CheckoutMessageBar checkedOutTo={existingActivity.createdBy} />
            )}
          {existingActivity?.mirroredActivityId && (
            <MirroredActivityMessageBar
              activityId={existingActivity.mirroredActivityId}
            />
          )}
          {hasMirroredChildren && (
            <ActivityWithMirroredChildrenMessageBar
              childActivityCount={
                existingActivity?.mirroredChildren?.length ?? 0
              }
            />
          )}
        </StickyTopBannerStack>
      )}
      <div className={classes.formContainer}>
        <div className={classes.editArea}>
          <form onSubmit={handleSubmit}>
            <div className={classes.formSpacingContainer}>
              <Field hint={t("activityForm.activityTitleFieldHint")}>
                <Label
                  size="medium"
                  weight="semibold"
                  htmlFor={activityTitleId}
                >
                  {t("activityForm.activityTitleFieldLabel")}
                </Label>
                <Input
                  size="large"
                  placeholder={t("activityForm.activityTitleFieldPlaceholder")}
                  id={activityTitleId}
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  onKeyDown={preventEnter}
                  disabled={!isCurrentUsersDraft || accessLevel < 3}
                />
              </Field>
            </div>
            <div className={classes.formSpacingContainer}>
              {!existingActivity?.mirroredActivityId && (
                <>
                  <Label weight="semibold">{t("content")}</Label>
                  <div className={contentClasses.activityContent}>
                    <MUIEditor
                      content={formData.richText}
                      onChange={handleRichTextChange}
                      disabled={!isCurrentUsersDraft}
                      onLinkToolInsert={handleLinkToolInsert}
                      onLinkToolRemove={handleLinkToolRemove}
                      selectedTools={selectedTools}
                      resolvedImages={resolvedImages}
                    />
                  </div>
                </>
              )}
              <div className={classes.activityStatusContainer}>
                {activeField && (
                  <div className={classes.checkboxContainer}>
                    <Switch
                      checked={
                        formData.fieldsWithValues?.[activeField.uniqueName] !==
                        undefined
                          ? (formData.fieldsWithValues?.[
                              activeField.uniqueName
                            ] as boolean)
                          : (activeField.defaultValue as boolean)
                      }
                      disabled={
                        !isCurrentUsersDraft ||
                        (formData.fieldsWithValues?.["archived"] as boolean) ===
                          true
                      }
                      label={activeField.displayName}
                      onChange={(
                        _e: ChangeEvent<HTMLInputElement>,
                        data: SwitchOnChangeData
                      ) =>
                        handleFieldChange(
                          activeField.uniqueName,
                          data.checked as boolean
                        )
                      }
                    />
                  </div>
                )}
                {archivedField && (
                  <div
                    key={archivedField.uniqueName}
                    className={classes.toggleContainer}
                  >
                    <Checkbox
                      checked={
                        formData.fieldsWithValues?.[
                          archivedField.uniqueName
                        ] !== undefined
                          ? (formData.fieldsWithValues?.[
                              archivedField.uniqueName
                            ] as boolean)
                          : (archivedField.defaultValue as boolean)
                      }
                      disabled={!isCurrentUsersDraft}
                      onChange={(
                        _ev: ChangeEvent<HTMLInputElement>,
                        data: CheckboxOnChangeData
                      ) =>
                        handleFieldChange(
                          archivedField.uniqueName,
                          data.checked as boolean
                        )
                      }
                      label={archivedField.displayName}
                      labelPosition="after"
                    />
                  </div>
                )}
              </div>
            </div>

            {shouldShowProcessFlowSection && (
              <Accordion
                collapsible
                openItems={processFlowSectionOpen ? ["1"] : []}
              >
                <AccordionItem value="1">
                  <AccordionHeader
                    onClick={() =>
                      handleAccordionSectionChange(
                        "processFlow",
                        !processFlowSectionOpen
                      )
                    }
                    className={classes.accordionHeader}
                    size="extra-large"
                    expandIconPosition="end"
                    expandIcon={
                      processFlowSectionOpen ? (
                        <ChevronDownFilled />
                      ) : (
                        <ChevronRightFilled />
                      )
                    }
                  >
                    {t("activitySectionProcessFlow")}
                  </AccordionHeader>
                  <AccordionPanel>
                    {!hasSelectedProcess ? (
                      <p>{t("processFlow.selectProcessMessage")}</p>
                    ) : (
                      <div className={classes.processFlowGrid}>
                        <div className={classes.processFlowFullWidth}>
                          <Field label={t("processFlow.head.activationLabel")}>
                            <Switch
                              checked={isProcessFlowHead}
                              disabled={!isCurrentUsersDraft}
                              onChange={(
                                _event: ChangeEvent<HTMLInputElement>,
                                data: SwitchOnChangeData
                              ) => handleProcessFlowHeadChange(data.checked)}
                              label={t(
                                isProcessFlowHead
                                  ? "processFlow.head.yes"
                                  : "processFlow.head.no"
                              )}
                            />
                          </Field>
                          {isProcessFlowHead && (
                            <div className={classes.processFlowOptions}>
                              <Field
                                label={t("processFlow.head.defaultShapeLabel")}
                              >
                                <Dropdown
                                  selectedOptions={[
                                    processFlowHeadSettings.defaultActivityShape,
                                  ]}
                                  value={getProcessFlowOptionLabel(
                                    processFlowHeadShapeOptions,
                                    processFlowHeadSettings.defaultActivityShape
                                  )}
                                  disabled={!isCurrentUsersDraft}
                                  onOptionSelect={(_event, data) => {
                                    if (data.optionValue) {
                                      handleProcessFlowHeadSettingChange(
                                        "defaultActivityShape",
                                        data.optionValue as ProcessFlowHeadSettings["defaultActivityShape"]
                                      );
                                    }
                                  }}
                                >
                                  {processFlowHeadShapeOptions.map((option) => (
                                    <Option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {t(option.labelKey)}
                                    </Option>
                                  ))}
                                </Dropdown>
                              </Field>
                              <Field
                                label={t(
                                  "processFlow.head.defaultConnectionLabel"
                                )}
                              >
                                <Dropdown
                                  selectedOptions={[
                                    String(
                                      processFlowHeadSettings.defaultShowArrowToNextStep
                                    ),
                                  ]}
                                  value={getProcessFlowOptionLabel(
                                    processConnectionOptions,
                                    processFlowHeadSettings.defaultShowArrowToNextStep
                                  )}
                                  disabled={!isCurrentUsersDraft}
                                  onOptionSelect={(_event, data) => {
                                    if (data.optionValue) {
                                      handleProcessFlowHeadSettingChange(
                                        "defaultShowArrowToNextStep",
                                        data.optionValue === "true"
                                      );
                                    }
                                  }}
                                >
                                  {processConnectionOptions.map((option) => (
                                    <Option
                                      key={String(option.value)}
                                      value={String(option.value)}
                                    >
                                      {t(option.labelKey)}
                                    </Option>
                                  ))}
                                </Dropdown>
                              </Field>
                            </div>
                          )}
                        </div>
                        <div className={classes.processFlowSectionDivider} />
                        <div className={classes.processFlowFullWidth}>
                          <Field label={t("processFlow.inheritance.label")}>
                            <Switch
                              checked={inheritProcessFlowSettings}
                              disabled={!isCurrentUsersDraft}
                              onChange={(
                                _event: ChangeEvent<HTMLInputElement>,
                                data: SwitchOnChangeData
                              ) =>
                                handleProcessFlowInheritanceChange(data.checked)
                              }
                              label={t(
                                inheritProcessFlowSettings
                                  ? "processFlow.inheritance.yes"
                                  : "processFlow.inheritance.no"
                              )}
                            />
                          </Field>
                          <div className={classes.processFlowOptions}>
                            <Field label={t("processFlow.activityShape.label")}>
                              <Dropdown
                                selectedOptions={[
                                  processFlowSettings.visibility,
                                ]}
                                value={getProcessFlowOptionLabel(
                                  processActivityShapeOptions,
                                  processFlowSettings.visibility
                                )}
                                disabled={
                                  !isCurrentUsersDraft ||
                                  inheritProcessFlowSettings
                                }
                                onOptionSelect={(_event, data) => {
                                  if (data.optionValue) {
                                    handleProcessFlowSettingChange(
                                      "visibility",
                                      data.optionValue as ProcessFlowSettings["visibility"]
                                    );
                                  }
                                }}
                              >
                                {processActivityShapeOptions.map((option) => (
                                  <Option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {t(option.labelKey)}
                                  </Option>
                                ))}
                              </Dropdown>
                            </Field>
                            <Field label={t("processFlow.connection.label")}>
                              <Dropdown
                                selectedOptions={[
                                  String(
                                    processFlowSettings.showArrowToNextStep
                                  ),
                                ]}
                                value={getProcessFlowOptionLabel(
                                  processConnectionOptions,
                                  processFlowSettings.showArrowToNextStep
                                )}
                                disabled={
                                  !isCurrentUsersDraft ||
                                  inheritProcessFlowSettings
                                }
                                onOptionSelect={(_event, data) => {
                                  if (data.optionValue) {
                                    handleProcessFlowSettingChange(
                                      "showArrowToNextStep",
                                      data.optionValue === "true"
                                    );
                                  }
                                }}
                              >
                                {processConnectionOptions.map((option) => (
                                  <Option
                                    key={String(option.value)}
                                    value={String(option.value)}
                                  >
                                    {t(option.labelKey)}
                                  </Option>
                                ))}
                              </Dropdown>
                            </Field>
                          </div>
                        </div>
                      </div>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}

            {accessLevel >= 3 && (
              <Accordion collapsible openItems={tagSectionOpen ? ["1"] : []}>
                <AccordionItem value="1">
                  <AccordionHeader
                    onClick={() =>
                      handleAccordionSectionChange("tags", !tagSectionOpen)
                    }
                    className={classes.accordionHeader}
                    size="extra-large"
                    expandIconPosition="end"
                    expandIcon={
                      tagSectionOpen ? (
                        <ChevronDownFilled />
                      ) : (
                        <ChevronRightFilled />
                      )
                    }
                  >
                    {t("activitySectionTags")}
                  </AccordionHeader>
                  <AccordionPanel>
                    {activityFields?.map((validField) => {
                      if (validField.type === 0 && validField.tagCollectionId) {
                        if (
                          validField.uniqueName === KmaChapterFieldUniqueName
                        ) {
                          return <Fragment key={validField.uniqueName} />;
                        }

                        return (
                          <div
                            key={validField.uniqueName}
                            className={classes.formSpacingContainer}
                          >
                            {isCurrentUsersDraft ? (
                              <VsaaTagPicker
                                tagCollectionId={validField.tagCollectionId}
                                label={validField.displayName}
                                onChange={(tagValue) =>
                                  handleFieldChange(
                                    validField.uniqueName,
                                    tagValue
                                  )
                                }
                                value={
                                  formData.fieldsWithValues?.[
                                    validField.uniqueName
                                  ] as VsaaTag[]
                                }
                                multiValue={validField.multiValue}
                                isRequired={validField.isRequired}
                                usedTagsId={filteredUsedPaths}
                                processWithSubActivitiesId={
                                  validField.uniqueName ===
                                    ProcessFieldUniqueName &&
                                  existingActivity?.processesIdsWithSubactivities
                                    ? existingActivity.processesIdsWithSubactivities
                                    : undefined
                                }
                                showPath={
                                  validField.uniqueName ===
                                  ProcessFieldUniqueName
                                }
                                error={null}
                                disabled={false}
                                disableRootNodes={
                                  validField.uniqueName ===
                                  ProcessFieldUniqueName
                                }
                              />
                            ) : (
                              <div>
                                <Label>{validField.displayName}</Label>
                                <DisplayField
                                  type={FieldTypes.Tag}
                                  label={validField.displayName}
                                  value={
                                    formData.fieldsWithValues?.[
                                      validField.uniqueName
                                    ] as VsaaTag[]
                                  }
                                  showPath={
                                    validField.uniqueName ===
                                    ProcessFieldUniqueName
                                  }
                                />
                              </div>
                            )}
                          </div>
                        );
                      }

                      return null;
                    })}

                    {accessLevel >= 3 && (
                      <div className={classes.formSpacingContainer}>
                        <p>
                          <Label weight="semibold">
                            {t("kmaChapterLabel")}
                          </Label>
                        </p>
                        {isCurrentUsersDraft ? (
                          <Dropdown
                            multiselect
                            selectedOptions={
                              (formData.fieldsWithValues?.[
                                KmaChapterFieldUniqueName
                              ] as string[]) ?? []
                            }
                            value={
                              chapters
                                ?.filter((chapter) =>
                                  (
                                    (formData.fieldsWithValues?.[
                                      KmaChapterFieldUniqueName
                                    ] as string[]) ?? []
                                  ).includes(chapter.id)
                                )
                                .map((chapter) => chapter.title)
                                .join(", ") ?? ""
                            }
                            onOptionSelect={(_event, data) =>
                              handleFieldChange(
                                KmaChapterFieldUniqueName,
                                data.selectedOptions
                              )
                            }
                            placeholder={t("selectChapters")}
                          >
                            {kmaVariantChapters.map((chapter) => (
                              <Option key={chapter.id} value={chapter.id}>
                                {chapter.title}
                              </Option>
                            ))}
                          </Dropdown>
                        ) : (
                          <span>
                            {(
                              chapters
                                ?.filter((chapter) =>
                                  (
                                    (formData.fieldsWithValues?.[
                                      KmaChapterFieldUniqueName
                                    ] as string[]) ?? []
                                  ).includes(chapter.id)
                                )
                                .map((chapter) => chapter.title) ?? []
                            ).join(", ") || "—"}
                          </span>
                        )}
                      </div>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}

            <Accordion collapsible openItems={toolsSectionOpen ? ["1"] : []}>
              <AccordionItem value="1">
                <AccordionHeader
                  onClick={() =>
                    handleAccordionSectionChange("tools", !toolsSectionOpen)
                  }
                  className={classes.accordionHeader}
                  size="extra-large"
                  expandIconPosition="end"
                  expandIcon={
                    toolsSectionOpen ? (
                      <ChevronDownFilled />
                    ) : (
                      <ChevronRightFilled />
                    )
                  }
                >
                  {t("activitySectionTools")}
                </AccordionHeader>
                <AccordionPanel>
                  {isCurrentUsersDraft && (
                    <ToolPickerDialog
                      selectedTools={selectedTools}
                      onSelect={(tool: Tool) => {
                        handleFileSelect(tool);
                      }}
                      onRemove={(toolId: string) => handleToolRemove(toolId)}
                    />
                  )}
                  <GroupedToolList
                    selectedTools={selectedTools}
                    onRemove={(id) => handleToolRemove(id)}
                    readonly={!isCurrentUsersDraft}
                  />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>

            {isCurrentUsersDraft ? (
              <>
                <div className={classes.actionButtons}>
                  <SaveSplitButton
                    size="large"
                    saveText={
                      existingActivity ? t("saveActivity") : t("createDraft")
                    }
                    onSave={handleSubmit}
                    saveDisabled={!saveEnabled}
                    isSaveLoading={activityLoading || activityRefetching}
                    showSaveAndPublishOption={
                      accessLevel >= 3 &&
                      Boolean(existingActivity) &&
                      saveEnabled &&
                      !isSubmitting &&
                      !activityLoading &&
                      !activityRefetching
                    }
                    saveAndPublishText={t("saveAndPublish")}
                    onSaveAndPublish={handleSaveAndPublish}
                    saveAndPublishDisabled={!saveEnabled}
                  />
                  {accessLevel >= 3 && existingActivity && (
                    <ActivityPublishDialog
                      versionId={existingActivity.versionId}
                      activityId={existingActivity.activityId}
                      disabled={saveEnabled}
                      openSignal={publishOpenSignal}
                      onPublishStart={() => setIsPublishing(true)}
                      onPublishFailed={() => setIsPublishing(false)}
                      pendingUpdate={pendingActivityUpdate}
                      mirroredIdsToClear={
                        existingActivity.mirroredChildren
                          ?.map((a) => a.activityId)
                          .filter((id): id is string => id !== undefined) ?? []
                      }
                    />
                  )}
                </div>

                {accessLevel >= 3 && formData.mirroredActivityId && (
                  <div className={classes.actionButtons}>
                    <Button
                      size="small"
                      onClick={() => {
                        setHasFormAction(true);
                        setFormData((prevFormData) => ({
                          ...prevFormData,
                          mirroredActivityId: null,
                        }));
                      }}
                    >
                      {t("demirrorActivity")}
                    </Button>
                  </div>
                )}

                {accessLevel >= 2 && existingActivity && (
                  <div className={classes.actionButtons}>
                    <Button
                      appearance="secondary"
                      onClick={handleCancelDraftClick}
                    >
                      {t("cancelDraft")}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                {accessLevel >= 3 && existingActivity && (
                  <div className={classes.actionButtons}>
                    <ActivityPublishDialog
                      versionId={existingActivity.versionId}
                      activityId={existingActivity.activityId}
                      disabled={saveEnabled}
                      openSignal={publishOpenSignal}
                      onPublishStart={() => setIsPublishing(true)}
                      onPublishFailed={() => setIsPublishing(false)}
                      pendingUpdate={pendingActivityUpdate}
                      mirroredIdsToClear={
                        existingActivity.mirroredChildren
                          ?.map((a) => a.activityId)
                          .filter((id): id is string => id !== undefined) ?? []
                      }
                      publishOnly={true}
                    />
                    <Button
                      appearance="secondary"
                      onClick={handleCancelDraftClick}
                    >
                      {t("cancelDraft")}
                    </Button>
                  </div>
                )}
              </>
            )}
          </form>
        </div>
        <div
          className={mergeClasses(
            classes.preview,
            contentClasses.activityContent
          )}
        >
          <Label weight="semibold">{t("activityPreviewLabel")}</Label>
          {!getTitleForPreview() && contentIsEmpty && (
            <div className={classes.previewPlaceholderText}>
              <p className="preview-placeholder">
                {t("activityPreviewPlaceholder")}
              </p>
            </div>
          )}
          <h1>{getTitleForPreview()}</h1>
          <div ref={contentRef}>{memoizedPreviewContent}</div>
        </div>
      </div>

      {accessLevel >= 2 && existingActivity && (
        <CancelDraftDialog
          isOpen={cancelDialogOpen}
          onCancel={handleCancelDraftCancel}
          onConfirm={handleCancelDraftConfirm}
          isLoading={cancelDraftPending}
          isWarning={!isCurrentUsersDraft}
        />
      )}

      {accessLevel >= 3 && existingActivity && (
        <CancelDraftDialog
          isOpen={cancelCheckedOutDialogOpen}
          onCancel={handleCancelCheckedOutDraftCancel}
          onConfirm={handleCancelCheckedOutDraftConfirm}
          isLoading={cancelDraftPending}
          isWarning
          title={t("cancelDraftDialogCheckedOutTitle")}
          message={t("cancelDraftDialogCheckedOutMessage", {
            user:
              existingActivity.createdBy?.displayName ||
              existingActivity.createdBy?.email ||
              t("unknownUser"),
          })}
        />
      )}

      {accessLevel >= 3 && warningDialogOpen && (
        <ActivityDeactivationWarningDialog
          isOpen={warningDialogOpen}
          onClose={handleWarningDialogClose}
          warningType={warningType}
        />
      )}

      <Dialog
        open={errorDialogOpen}
        onOpenChange={(_event, data) => setErrorDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t("errorInactivation")}</DialogTitle>
            <DialogContent>
              {(() => {
                switch (errorType) {
                  case "mirroredChildren":
                    return t("cannotInactivateSourceActivity");
                  case "subActivities":
                    return t("cannotModifyActivityWithSubActivities");
                  case "mirroredChildrenProcess":
                  default:
                    return t("mirroredChildrenProcess");
                }
              })()}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleErrorDialogClose}>
                {t("ok")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ActivityForm;
